import { AddCircle, CalendarMonth, Delete } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import { Autocomplete, Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import fetchAvailabilities from "api/availability/fetchAvailabilities";
import updateAvailabilityBulk from "api/availability/updateAvailabilityBulk";
import ConfirmationModal from "components/ConfirmationModal";
import GenericModal from "components/GenericModal";
import JobAvailabilityCalendar from "components/JobAvailabilityCreateModal/JobAvailabilityCalendar";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import FormField from "layouts/applications/wizard/components/FormField";
import fetchEvents from "layouts/pages/events/actions/fetchEvents";
import fetchUsers from "layouts/pages/profile/actions/fetchUsers";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { mapObject } from "utils/helpers/arrays";

const ProfileAvailabilityEditorModal = ({
  open,
  onClose,
  onSubmit,
  currentUser,
  allowSelectOther,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { venues, allVenues, currentLoggedUser, company, companyType, userType } =
    useAppContextController();

  const calendarApi = useRef(null);
  const bindCalendarApi = (api) => {
    calendarApi.current = api;
  };
  const copyFuncRef = useRef(null);
  const pasteFuncRef = useRef(null);
  const lastKeydownEventRef = useRef(null);

  // Index of fetched startDates
  // Don't refetch dates of a week if they have already been fetched
  // Otherwise it will overwrite local data
  const fetchedDates = useRef({});

  const [slotList, setSlotList] = useState([]);
  const [copiedWeek, setCopiedWeek] = useState(null);
  const [recruiterToSelect, setRecruiterToSelect] = useState(null);
  const [currentDates, setCurrentDates] = useState({ start: "", end: "" });

  const allOptionsUsers = { fetchAll: true, filters: { userType: "Master;Admin" } };

  const { data: users, isLoading: isLoadingUsers } = useQuery(
    ["users", allOptionsUsers],
    () => fetchUsers(allOptionsUsers),
    { enabled: allowSelectOther }
  );

  const dropdownOptions = useMemo(
    () =>
      (users?.data || []).map((rec) => ({
        ...rec,
        label: `${rec.firstName} ${rec.lastName}`,
        value: rec._id,
      })),
    [users?.data]
  );

  const [selectedRecruiter, setSelectedRecruiter] = useState({
    ...currentUser,
    value: currentUser?._id,
    label: `${currentUser?.firstName} ${currentUser?.lastName}`,
  });

  const fullReset = () => {
    fetchedDates.current = {};
    queryClient.invalidateQueries(["availability"]);
    setSlotList([]);
    setCopiedWeek(null);
  };

  useEffect(() => {
    if (open) {
      fullReset();
      setSelectedRecruiter({
        ...currentUser,
        value: currentUser?._id,
        label: `${currentUser?.firstName} ${currentUser?.lastName}`,
      });
    }
  }, [open]);

  const isReadOnly = userType !== "Master" && currentLoggedUser?._id !== selectedRecruiter?.value;

  const recruiterInterviews = useMemo(() => {
    if (selectedRecruiter) {
      if (users?.data) {
        const currentRecruiterId = selectedRecruiter?._id;
        const currentRecruiterUpdated = users?.data.find((rec) => rec._id === currentRecruiterId);
        if (currentRecruiterUpdated) return currentRecruiterUpdated.interviews ?? [];
      }
      return selectedRecruiter.interviews ?? [];
    }
    return [];
  }, [selectedRecruiter, users?.data]);

  const handleDatesSet = (dateInfo) => {
    setCurrentDates({
      start: dateInfo.start?.toISOString() ?? "",
      end: dateInfo.end?.toISOString() ?? "",
    });
  };

  const options = useMemo(() => {
    return {
      fetchAll: true,
      filters: { userId: selectedRecruiter?._id },
      startDate: currentDates?.start,
      endDate: currentDates?.end,
    };
  }, [currentDates?.end, currentDates?.start, selectedRecruiter?._id]);

  const {
    data: availability = [],
    isLoading = true,
    isFetching,
  } = useQuery(
    ["availability", currentDates?.start, currentDates?.end, selectedRecruiter?._id],
    () => fetchAvailabilities(options),
    {
      enabled: !!currentDates?.start && !!currentDates?.end && !!selectedRecruiter?._id,
      onSettled: (data) => {
        if (!fetchedDates.current[currentDates?.start]) {
          if (data.data) {
            const rawSlots = data.data;
            setSlotList((slots) => [
              ...slots,
              ...rawSlots.map((sl) => ({
                id: sl._id,
                title: "EXISTING_SLOT",
                start: sl.startDate,
                end: sl.endDate,
                className: "info",
              })),
            ]);
          }
          fetchedDates.current[currentDates?.start] = true;
        }
      },
      refetchOnWindowFocus: false,
    }
  );

  const optionsEvents = useMemo(() => {
    if (companyType === "Company") {
      return {
        fetchAll: true,
        filters: {
          status: "Active",
          eventUrl:
            recruiterInterviews
              .filter((int) => int.blocksAvailability === "Yes")
              .map((int) => int.eventUrl)
              .join(";") || "NONE",
        },
        startDate: currentDates?.start,
        endDate: currentDates?.end,
      };
    }

    return {
      fetchAll: true,
      filters: {
        eventType: "Screening Interview",
        status: "Active",
        "recruiter.userId": selectedRecruiter?._id,
      },
      startDate: currentDates?.start,
      endDate: currentDates?.end,
    };
  }, [
    companyType,
    currentDates?.end,
    currentDates?.start,
    recruiterInterviews,
    selectedRecruiter?._id,
  ]);

  const {
    data: interviewEvents = [],
    isLoading: eventsIsLoading = true,
    isFetching: eventsIsFetching,
  } = useQuery(
    ["events", currentDates?.start, currentDates?.end, selectedRecruiter?._id, optionsEvents],
    () => fetchEvents(optionsEvents),
    {
      enabled: !!currentDates?.start && !!currentDates?.end && !!selectedRecruiter?._id,
      refetchOnWindowFocus: false,
    }
  );

  const interviewSlotList = useMemo(() => {
    if (interviewEvents?.data) {
      const rawSlots = interviewEvents.data;
      return rawSlots.map((sl) => ({
        id: sl._id,
        title: "EVENT_SLOT",
        start: sl.eventDate,
        end: sl.eventEndTime,
        className: "warning",
        description: (
          <Box>
            {sl.eventName}
            <br />
            {moment(sl.eventDate).format("MM/DD/YYYY")}
            <br />
            {`${moment(sl.eventDate).format("hh:mm A")}-${moment(sl.eventEndTime).format(
              "hh:mm A"
            )}`}
          </Box>
        ),
        link: `/events/${sl.eventUrl}/action/eventsinfo`,
      }));
    }
    return [];
  }, [interviewEvents?.data]);

  const { mutate: updateBulk } = useMutation(updateAvailabilityBulk, {
    onSuccess: (data, vars) => {
      const { insertedIds } = data;
      const { skipLocalUpdate } = vars;
      let insertedIdIndex = 0;
      if (!skipLocalUpdate) {
        setSlotList(
          slotList.flatMap((slot) => {
            if (slot.title === "NEW_SLOT") {
              const currentId = insertedIdIndex;
              insertedIdIndex += 1;

              return [
                {
                  ...slot,
                  id: insertedIds[currentId],
                  title: "EXISTING_SLOT",
                  className: "info",
                },
              ];
            }
            if (slot.title === "DELETED_SLOT") {
              return [];
            }
            return [slot];
          })
        );
      }

      onSubmit?.();

      enqueueSnackbar("Changes have been saved successfully", { variant: "success" });
    },
    onError: (e) => {
      enqueueSnackbar(
        e?.response?.data?.message === "Nothing to update"
          ? e?.response?.data?.message
          : "An error has occurred updating availability",
        { variant: "error" }
      );
    },
  });

  const getCurrentWeekEvents = () => {
    if (calendarApi.current) {
      // Get the view's visible start and end dates
      const { view } = calendarApi.current;
      const startDate = view.activeStart;
      const endDate = view.activeEnd;

      // Get all events within the date range
      const events = calendarApi.current.getEvents().filter((event) => {
        const eventStart = event.start;
        const eventEnd = event.end || eventStart; // If no end time, use start time

        // Check if the event overlaps with the visible range
        return eventStart < endDate && eventEnd >= startDate;
      });

      return events;
    }
    return [];
  };

  const handleChangeRecruiter = (newRecruiter) => {
    if (
      selectedRecruiter?._id &&
      slotList.find((slot) => slot.title === "NEW_SLOT" || slot.title === "DELETED_SLOT")
    ) {
      setRecruiterToSelect(newRecruiter);
      return;
    }
    fullReset();
    setSelectedRecruiter(newRecruiter);
  };

  const handleCancel = () => {
    onClose(false);
  };

  const submitChanges = (skipLocalUpdate) => {
    if (isReadOnly) return null;
    const data = slotList.flatMap((slot) => {
      if (slot.title === "NEW_SLOT") {
        return [
          {
            startDate: moment(slot.start).toISOString(),
            endDate: moment(slot.end).toISOString(),
            applicantId: selectedRecruiter?.applicantId,
            userId: selectedRecruiter?._id,
            action: "create",
          },
        ];
      }
      if (slot.title === "DELETED_SLOT") {
        return [
          {
            _id: slot.id,
            action: "delete",
          },
        ];
      }
      return [];
    });

    return updateBulk({ slots: data, skipLocalUpdate });
  };

  const handleCopy = () => {
    if (isReadOnly) return;
    if (isFetching) return;
    const currentWeekEvents = getCurrentWeekEvents() ?? [];
    const validCurrentWeekEvents = currentWeekEvents.filter((ev) => ev.title !== "DELETED_SLOT");
    if (!validCurrentWeekEvents.length) {
      enqueueSnackbar("Nothing to copy", { variant: "error" });
      return;
    }
    setCopiedWeek(validCurrentWeekEvents);
    enqueueSnackbar("Week slots copied", { variant: "success" });
  };

  const handlePaste = () => {
    if (isReadOnly) return;
    if (isFetching) return;
    if (!copiedWeek) return;
    const currentWeekEvents = getCurrentWeekEvents() ?? [];
    const currentWeekEventIds = currentWeekEvents.map((ev) => ev.id.toString());
    const validCurrentWeekEvents = currentWeekEvents.filter((ev) => ev.title !== "DELETED_SLOT");
    const validCurrentWeekEventIds = validCurrentWeekEvents.map((ev) => ev.id.toString());

    const newSlots = [...slotList].flatMap((slot) => {
      if (currentWeekEventIds.includes(slot.id.toString()) && slot.title === "EXISTING_SLOT") {
        return [
          {
            ...slot,
            title: "DELETED_SLOT",
            className: "error",
            display: "background",
          },
        ];
      }
      if (!validCurrentWeekEventIds.includes(slot.id.toString())) {
        return [slot];
      }
      return [];
    });

    const currentViewStart = moment(calendarApi.current.view.activeStart);
    // For each copied event, create a new event in the current week
    copiedWeek.forEach((originalEvent) => {
      // Get the day of week (0-6) and time from the original event
      const originalStart = moment(originalEvent.start);
      const originalEnd = moment(originalEvent.end);

      const dayOfWeek = originalStart.day(); // 0 for Sunday, 1 for Monday, etc.

      // Calculate the date in the current week that corresponds to the same day of week
      const newStartDate = moment(currentViewStart)
        .add(dayOfWeek, "days")
        .hour(originalStart.hour())
        .minute(originalStart.minute())
        .second(originalStart.second());

      // Calculate the duration of the original event
      const duration = moment.duration(originalEnd.diff(originalStart));

      // Apply the same duration to calculate the new end time
      const newEndDate = moment(newStartDate).add(duration);

      // Add the new event to the calendar
      newSlots.push({
        id: Math.round(Math.random() * 10000),
        title: "NEW_SLOT",
        start: newStartDate.toISOString(),
        end: newEndDate.toISOString(),
        className: "success",
      });
    });

    setSlotList(newSlots);
    enqueueSnackbar("Week slots pasted", { variant: "success" });
  };

  copyFuncRef.current = handleCopy;
  pasteFuncRef.current = handlePaste;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === "c") {
        event.preventDefault();
        if (copyFuncRef.current) copyFuncRef.current();
      }

      if (event.ctrlKey && event.key === "v") {
        event.preventDefault(); // Prevent default paste behavior
        if (pasteFuncRef.current) pasteFuncRef.current();
      }
    };

    if (open) {
      // Add event listener when component mounts
      document.addEventListener("keydown", handleKeyDown);
      lastKeydownEventRef.current = handleKeyDown;
    } else if (lastKeydownEventRef.current) {
      document.removeEventListener("keydown", lastKeydownEventRef.current);
      lastKeydownEventRef.current = null;
    }

    // Remove event listener when component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      lastKeydownEventRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseConfirmationModal = () => {
    fullReset();
    setSlotList([]);
    setSelectedRecruiter(recruiterToSelect);
    setRecruiterToSelect(null);
  };

  const handleSaveConfirmationModal = async (e) => {
    e.stopPropagation();
    await submitChanges(true);
    handleCloseConfirmationModal();
  };

  const header = (
    <>
      <Stack display="flex" flexDirection="row" justifyContent="space-between">
        <MDTypography variant="h4">Availability Editor</MDTypography>
        <CancelIcon
          onClick={() => {
            handleCancel();
          }}
          sx={{ color: "#AEAEAE", cursor: "pointer" }}
        />
      </Stack>
    </>
  );

  const buttons = (
    <Grid container xs={12} direction="row" spacing={2} px={2} justifyContent="space-between">
      <Box>
        <Button
          variant="contained"
          style={{ color: "white" }}
          sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
          onClick={() => {
            handleCopy();
          }}
        >
          Copy
        </Button>
        <Button
          variant="contained"
          style={{ color: "white" }}
          sx={{ padding: "0.5rem 1rem", borderRadius: 1, marginLeft: 2 }}
          disabled={!copiedWeek}
          onClick={() => {
            handlePaste();
          }}
        >
          Paste
        </Button>
      </Box>
      <Button
        variant="contained"
        style={{ color: "white" }}
        sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
        onClick={() => {
          submitChanges();
        }}
      >
        Save
      </Button>
    </Grid>
  );

  const modalBody = (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(70vh - 100px)",
          // overflowY: "hidden",
        }}
      >
        <Box sx={{ width: "25%" }}>
          {allowSelectOther && (
            <Autocomplete
              options={dropdownOptions.sort((a, b) => (a.firstName > b.firstName ? 1 : -1)) || []}
              disableClearable
              autoSelect
              defaultValue={null}
              value={selectedRecruiter}
              onChange={(e, v) => {
                handleChangeRecruiter(v);
              }}
              renderInput={(params) => <FormField {...params} type="text" label="Recruiter" />}
              isOptionEqualToValue={(option, value) => {
                return option._id === value._id;
              }}
              getOptionKey={(option) => option.value}
            />
          )}
        </Box>

        <Box sx={{ flexGrow: 1, flexBasis: 0, flexShrink: 0, overflowY: "auto" }}>
          <JobAvailabilityCalendar
            slotList={slotList}
            setSlotList={setSlotList}
            interviewSlotList={interviewSlotList}
            bindCalendarApi={bindCalendarApi}
            selectedRecruiter={selectedRecruiter}
            onDatesSet={handleDatesSet}
            isLoading={isFetching}
            isReadOnly={isReadOnly}
          />
        </Box>
        <Box my={1} px={1}>
          <Typography variant="h5">Legend</Typography>
          <Grid container xs={12}>
            <Grid container item xs={6} sm={3} alignItems="center">
              <Paper
                sx={{
                  backgroundColor: "info.main",
                  display: "inline-flex",
                  marginRight: 1,
                }}
              >
                <AddCircle color="white" fontSize="medium" sx={{ opacity: 0 }} />
              </Paper>
              <Typography variant="text">Existing Slot</Typography>
            </Grid>
            <Grid container item xs={6} sm={3} alignItems="center">
              <Paper
                sx={{
                  backgroundColor: "success.main",
                  display: "inline-flex",
                  marginRight: 1,
                }}
              >
                <AddCircle color="white" fontSize="medium" />
              </Paper>
              <Typography variant="text">Added Slot</Typography>
            </Grid>
            <Grid container item xs={6} sm={3} alignItems="center">
              <Paper
                sx={{
                  backgroundColor: "error.main",
                  display: "inline-flex",
                  marginRight: 1,
                  opacity: 0.5,
                }}
              >
                <Delete color="white" fontSize="medium" />
              </Paper>
              <Typography variant="text">Deleted Slot</Typography>
            </Grid>
            <Grid container item xs={6} sm={3} alignItems="center">
              <Paper
                sx={{
                  backgroundColor: "warning.main",
                  display: "inline-flex",
                  marginRight: 1,
                }}
              >
                <CalendarMonth color="white" fontSize="medium" />
              </Paper>
              <Typography variant="text">{`Existing ${
                companyType === "Company" ? "Interview" : "Event"
              }`}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <GenericModal
        open={open}
        setOpen={onClose}
        body={modalBody}
        header={header}
        buttons={isReadOnly ? null : buttons}
        sx={(theme) => ({
          width: "90% !important",
          maxWidth: "940px !important",
          [theme.breakpoints.down("md")]: {
            "& .MuiCardContent-root": {
              padding: "0px",
            },
          },
        })}
      />
      <ConfirmationModal
        isOpen={!!recruiterToSelect}
        onClose={handleCloseConfirmationModal}
        title={`Do you want to save changes for ${selectedRecruiter?.label}?`}
        onConfirm={handleSaveConfirmationModal}
      />
    </>
  );
};

export default ProfileAvailabilityEditorModal;
